// The text of a Proposal message as it leaves the building. Kept apart from the Share panel so the exact
// rule is unit-tested: EmmA's words (money lines removed — she never states figures), then the RELEASED
// amount, then the private link.

/** Removes any line EmmA wrote that states a figure or a GST phrase: the amount line is ours, not hers. */
export const removeMoneyLines = (message: string) => message.split('\n').filter(line => !/\$\s*\d|\b(?:incl(?:uding)?|inc(?:lusive)?)\.?\s*GST\b/i.test(line)).join('\n').trim();

const money = (n: number) => n.toLocaleString('en-AU', {minimumFractionDigits: 2, maximumFractionDigits: 2});
/**
 * THE MESSAGE THAT LEAVES: EmmA's words, then the released amount, then the private link — always in that
 * order, and the same text on the review screen as in the saved draft. The amount and the link are added
 * here, never trusted to generated prose (audit 26 Sep: she could omit the link, and the amount came from the
 * working draft while the link opened the older released copy).
 */
/**
 * THE LINES THE EMAIL STATES (Jacky, 26 Oct 2026: "email message is wrong, should list out line details, and the budget for levelling
 * should not be added to total"). Built by the backbone from the released copy (`roomChoiceSummary`), never from prose: each step and
 * its figure, the price, then what is provisional — priced and agreed now, charged only if used — outside the price.
 */
export type ReleasedSummary = {steps: {label: string; amount: number}[]; price: number; provisional: {label: string; amount: number | null}[]; closing: 'accept_on_page' | 'call'};
export type ReleasedAmount = {total_ex_gst: number | null; summary?: ReleasedSummary | null};
const amountLines = (released: ReleasedAmount | null): string => {
  if (!released) return '';
  const s = released.summary;
  if (!s) return released.total_ex_gst === null ? '' : `$${money(released.total_ex_gst)} + GST`;
  const steps = s.steps.map((x) => `${x.label}: $${money(x.amount)} + GST`);
  const price = `Your price: $${money(s.price)} + GST`;
  const provisional = s.provisional.length ? [`Only if needed, approved by you on the day (not in the price):`, ...s.provisional.map((p) => `· ${p.label}: ${p.amount === null ? 'priced then' : `up to $${money(p.amount)} + GST`}`)] : [];
  return [...steps, price, ...provisional].join('\n');
};
export function approvedMessage(body: string, released: number | ReleasedAmount | null, link: string | null, channel: 'email' | 'sms' = 'email'): string {
  const words = removeMoneyLines(body);
  const rel: ReleasedAmount | null = typeof released === 'number' ? {total_ex_gst: released} : released;
  const amount = amountLines(rel);
  const withLink = link && !words.includes(link) ? link : '';
  // HOW TO GO AHEAD, in every Proposal email (Jacky, 16 Oct 2026: "advise customer can accept this on the interactive
  // page or contact Jacky"; 26 Oct 2026 for a room-choice Proposal that closes by a call: "just call Jacky and we will work
  // with you along your renovation schedule"). Ours, like the amount and the link; a text stays short. Customer-facing, so English.
  const closeLine = rel?.summary?.closing === 'call' ? CALL_LINE : ACCEPT_LINE;
  const close = channel === 'email' && link && !words.includes(ACCEPT_PHONE) ? closeLine : '';
  return [words, amount, withLink, close].filter(Boolean).join('\n\n');
}
const ACCEPT_PHONE = '0432 399 295';
export const ACCEPT_LINE = `You can accept this Proposal on the page above, or contact Jacky on ${ACCEPT_PHONE}.`;
export const CALL_LINE = `To go ahead, call Jacky on ${ACCEPT_PHONE}. We plan the floors into your renovation and work to your schedule.`;
